<template>
  <div
    class="glow-ring"
    :class="[`glow-ring--${intensity}`, { 'glow-ring--spin': spin, 'glow-ring--pulse': pulse }]"
    :style="{ width: `${size}px`, height: `${size}px` }"
    aria-hidden="true"
  >
    <svg class="glow-ring__svg" :viewBox="`0 0 ${VB} ${VB}`">
      <defs>
        <radialGradient :id="`${uid}-core`" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="rgba(221,245,90,0.16)" />
          <stop offset="55%" stop-color="rgba(221,245,90,0.05)" />
          <stop offset="100%" stop-color="rgba(221,245,90,0)" />
        </radialGradient>
        <filter :id="`${uid}-blur`" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3.5" />
        </filter>
      </defs>
      <circle :cx="C" :cy="C" :r="C" :fill="`url(#${uid}-core)`" />
      <circle
        v-for="ring in ringList"
        :key="ring.r"
        :cx="C"
        :cy="C"
        :r="ring.r"
        fill="none"
        stroke="#DDF55A"
        :stroke-opacity="ring.opacity"
        :stroke-width="ring.width"
      />
      <g v-if="arc" class="glow-ring__arc">
        <circle
          :cx="C"
          :cy="C"
          :r="arcR"
          fill="none"
          stroke="#DDF55A"
          stroke-width="9"
          stroke-linecap="round"
          :stroke-dasharray="arcDash"
          :filter="`url(#${uid}-blur)`"
          opacity="0.8"
        />
        <circle
          :cx="C"
          :cy="C"
          :r="arcR"
          fill="none"
          stroke="#DDF55A"
          stroke-width="5"
          stroke-linecap="round"
          :stroke-dasharray="arcDash"
        />
      </g>
    </svg>
    <div v-if="$slots.default" class="glow-ring__content">
      <slot />
    </div>
  </div>
</template>

<script setup>
/**
 * GlowRing — декоративные концентрические светящиеся кольца (поиск, hero, «VS», отсчёт).
 *
 * <GlowRing :size="300" arc spin />                         // экран 1/3: кольца + вращающаяся дуга
 * <GlowRing :size="120" :rings="2" intensity="strong">7</GlowRing>  // центр со слотом (отсчёт)
 *
 * props: size (px, 240), rings (кол-во колец, 3), arc (лаймовые дуги), spin (вращение дуг),
 *        pulse (пульсация свечения), intensity ('soft'|'strong').
 * Слот default — контент по центру. Учитывает prefers-reduced-motion.
 */
import { computed } from "vue";

const props = defineProps({
  size: { type: Number, default: 240 },
  rings: { type: Number, default: 3 },
  arc: { type: Boolean, default: false },
  spin: { type: Boolean, default: false },
  pulse: { type: Boolean, default: false },
  intensity: { type: String, default: "soft", validator: (v) => ["soft", "strong"].includes(v) },
});

const VB = 200;
const C = VB / 2;
const uid = `gr${Math.random().toString(36).slice(2, 9)}`;

const ringList = computed(() => {
  const strong = props.intensity === "strong";
  return Array.from({ length: Math.max(1, props.rings) }, (_, i) => {
    const r = C - 4 - i * ((C - 30) / Math.max(1, props.rings));
    return {
      r: Math.round(r * 10) / 10,
      opacity: (strong ? 0.5 : 0.22) * (1 - i * 0.18),
      width: i === 0 ? 1 : 1.5,
    };
  });
});

const arcR = computed(() => Math.round(C * 0.62));
const arcDash = computed(() => {
  const circumference = 2 * Math.PI * arcR.value;
  const seg = circumference * 0.22;
  const gap = circumference * 0.28;
  return `${seg.toFixed(1)} ${gap.toFixed(1)}`;
});
</script>

<style scoped>
.glow-ring {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.glow-ring__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.glow-ring--strong .glow-ring__svg {
  filter: drop-shadow(0 0 10px rgba(221, 245, 90, 0.35));
}

.glow-ring__arc {
  transform-origin: 50% 50%;
  transform-box: view-box;
}

.glow-ring--spin .glow-ring__arc {
  animation: cb-spin 6s linear infinite;
}

.glow-ring--pulse .glow-ring__svg {
  animation: cb-glow-breathe 2.8s ease-in-out infinite;
}

.glow-ring__content {
  position: relative;
  z-index: 1;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (prefers-reduced-motion: reduce) {
  .glow-ring__arc,
  .glow-ring__svg {
    animation: none !important;
  }
}
</style>
